import React, { useEffect, useMemo, useState } from "react";
import { MapPin, X } from "lucide-react";
import api from "@/lib/api";
import DealerCard from "@/components/site/DealerCard";
import NearestOutletWidget from "@/components/site/NearestOutletWidget";
import PageSeo from "@/components/site/PageSeo";
import { useSettings, usePageCopy } from "@/contexts/SettingsContext";
import { selectOption } from "@/lib/selectOption";

const DealersPage = () => {
    const [dealers, setDealers] = useState([]);
    const [city, setCity] = useState("");
    const [brand, setBrand] = useState("");
    const [allCities, setAllCities] = useState([]);
    const [allBrands, setAllBrands] = useState([]);
    const copy = usePageCopy("dealers", { title: "Find a Servall outlet" });
    const { pages = {} } = useSettings() || {};

    useEffect(() => {
        api.get("/dealers").then(r => {
            const list = Array.isArray(r.data) ? r.data : [];
            setAllCities(Array.from(new Set(list.map(d => d.city))).sort());
            setAllBrands(Array.from(new Set(list.map(d => d.brand))).sort());
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (city) params.set("city", city);
        if (brand) params.set("brand", brand);
        api.get(`/dealers?${params.toString()}`).then(r => setDealers(Array.isArray(r.data) ? r.data : [])).catch(() => setDealers([]));
    }, [city, brand]);

    const grouped = useMemo(() => {
        const m = {};
        dealers.forEach(d => { (m[d.city] = m[d.city] || []).push(d); });
        return m;
    }, [dealers]);

    const canClear = Boolean(city || brand);
    const countLabel = `${dealers.length} outlet${dealers.length === 1 ? "" : "s"}`;

    return (
        <div className="bg-[#F4F6F8]" data-testid="dealers-page">
            <PageSeo pageKey="dealers" />
            <div className="bg-[#0A2540] text-white">
                <div className="container-x py-8 md:py-10" data-testid="dealers-header">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#E31837] flex items-center justify-center shrink-0">
                            <MapPin size={20} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-white/60">{copy.eyebrow || "Outlets"}</p>
                    </div>
                    <h1 className="font-display font-bold text-[1.65rem] leading-[1.15] sm:text-3xl mt-3 max-w-3xl" style={{ textWrap: "balance" }}>
                        {copy.title}
                    </h1>
                    {copy.subtitle ? (
                        <p className="text-sm leading-relaxed text-white/75 mt-2 max-w-2xl" style={{ textWrap: "pretty" }}>
                            {copy.subtitle}
                        </p>
                    ) : null}
                    <p className="text-sm text-white/70 mt-2">{countLabel}</p>
                </div>
            </div>

            <section className="container-x py-6 md:py-8">
                <NearestOutletWidget
                    brand={brand}
                    limit={3}
                    compact
                    embedded
                    thanks={pages.dealers_thanks || "Thanks, {name}. Your details are saved."}
                >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="dealers-filters">
                        <div className="sm:flex-1 sm:min-w-0">
                            <select className="input input-compact" value={city} onChange={(e) => setCity(e.target.value)} data-testid="dealer-city" aria-label="City">
                                <option value="">All cities</option>
                                {allCities.map((c) => selectOption(c))}
                            </select>
                        </div>
                        <div className="sm:flex-1 sm:min-w-0">
                            <select className="input input-compact" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="dealer-brand" aria-label="Brand">
                                <option value="">All brands</option>
                                {allBrands.map((b) => selectOption(b))}
                            </select>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                            <span className="text-sm font-semibold text-[#0A2540] whitespace-nowrap">{countLabel}</span>
                            {canClear && (
                                <button
                                    type="button"
                                    onClick={() => { setCity(""); setBrand(""); }}
                                    className="shrink-0 whitespace-nowrap text-[11px] text-[#E31837] font-semibold inline-flex items-center gap-1 h-7 px-2"
                                    data-testid="dealers-clear"
                                >
                                    <X size={12} /> Clear
                                </button>
                            )}
                        </div>
                    </div>
                </NearestOutletWidget>

                {dealers.length === 0 ? (
                    <p className="text-sm text-[#7a8597] mt-4">No outlets found for this filter.</p>
                ) : (
                    <div className="mt-4 space-y-6">
                        {Object.keys(grouped).sort().map(cityName => (
                            <div key={cityName}>
                                <h2 className="font-display font-bold text-lg text-[#0A2540] mb-3 flex items-center gap-2">
                                    {cityName}
                                    <span className="chip">{grouped[cityName].length}</span>
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                                    {grouped[cityName].map(d => (
                                        <DealerCard key={d.id} dealer={d} plain />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default DealersPage;
